import { Vector3, type CameraView, type Vector3Like } from '@/math'
import { Position } from '../voxelMath'

export type Aabb = { min: Position; max: Position }

export type ScreenPoint = { x: number; y: number; z: number }
export type ScreenRect = { minX: number; maxX: number; minY: number; maxY: number }

// ---- Projection utilities ----
export function worldToScreen(camera: CameraView, v: Vector3Like, rect: DOMRect): ScreenPoint {
  const p = new Vector3().copy(v).project(camera)
  const x = (p.x * 0.5 + 0.5) * rect.width
  const y = (1 - (p.y * 0.5 + 0.5)) * rect.height
  return { x, y, z: p.z }
}

export function screenToWorldAtDepth(
  camera: CameraView,
  screenX: number,
  screenY: number,
  depthNdc: number,
  rect: DOMRect,
): Vector3 {
  const ndcX = (screenX / rect.width) * 2 - 1
  const ndcY = -(screenY / rect.height) * 2 + 1
  return new Vector3(ndcX, ndcY, depthNdc).unproject(camera)
}

export function getWorldUnitsPerPixel(
  camera: CameraView,
  rect: DOMRect,
  distanceToCamera: number,
): number {
  const isPerspective = camera.isPerspectiveCamera
  const worldHeightAtDepth = isPerspective
    ? 2 * Math.tan(((camera.fov ?? 50) * Math.PI) / 180 / 2) * distanceToCamera
    : (camera.top ?? 1) - (camera.bottom ?? -1)
  const pixelsPerWorldUnit = rect.height / worldHeightAtDepth
  return 1 / pixelsPerWorldUnit
}

export type Axis = 'x' | 'y' | 'z'

export function screenAxisAlignmentChoice(
  camera: CameraView,
  rect: DOMRect,
  anchorCenter: Position,
  pointerX: number,
  pointerY: number,
): { axis: Axis, dotX: number, dotY: number, dotZ: number } {
  const a = worldToScreen(camera, new Vector3(anchorCenter.x, anchorCenter.y, anchorCenter.z), rect)
  const step = (dx: number, dy: number, dz: number) => worldToScreen(
    camera,
    new Vector3(anchorCenter.x + dx, anchorCenter.y + dy, anchorCenter.z + dz),
    rect,
  )
  const ux = pointerX - a.x
  const uy = pointerY - a.y
  const lu = Math.hypot(ux, uy) || 1
  const Ux = ux / lu
  const Uy = uy / lu
  const sx = step(1, 0, 0)
  const vxX = sx.x - a.x, vyX = sx.y - a.y
  const lx = Math.hypot(vxX, vyX) || 1
  const dotX = Math.abs((vxX / lx) * Ux + (vyX / lx) * Uy)
  const sy = step(0, 1, 0)
  const vxY = sy.x - a.x, vyY = sy.y - a.y
  const ly = Math.hypot(vxY, vyY) || 1
  const dotY = Math.abs((vxY / ly) * Ux + (vyY / ly) * Uy)
  const sz = step(0, 0, 1)
  const vxZ = sz.x - a.x, vyZ = sz.y - a.y
  const lz = Math.hypot(vxZ, vyZ) || 1
  const dotZ = Math.abs((vxZ / lz) * Ux + (vyZ / lz) * Uy)
  let axis: Axis = 'x'
  if (dotY >= dotX && dotY >= dotZ) axis = 'y'
  else if (dotZ >= dotX && dotZ >= dotY) axis = 'z'
  return { axis, dotX, dotY, dotZ }
}

export function screenAxisDistanceChoice(
  camera: CameraView,
  rect: DOMRect,
  anchorCenter: Position,
  pointerX: number,
  pointerY: number,
  L: number = 24,
): { axis: Axis, dX: number, dY: number, dZ: number } {
  const step = (dx: number, dy: number, dz: number) => worldToScreen(
    camera,
    new Vector3(anchorCenter.x + dx, anchorCenter.y + dy, anchorCenter.z + dz),
    rect,
  )
  const xA = step(-L, 0, 0), xB = step(L, 0, 0)
  const yA = step(0, -L, 0), yB = step(0, L, 0)
  const zA = step(0, 0, -L), zB = step(0, 0, L)
  const P = { x: pointerX, y: pointerY }
  const segDist = (a: {x:number;y:number}, b: {x:number;y:number}, p: {x:number;y:number}) => {
    const vx = b.x - a.x, vy = b.y - a.y
    const wx = p.x - a.x, wy = p.y - a.y
    const vv = vx*vx + vy*vy || 1
    const t = Math.max(0, Math.min(1, (wx*vx + wy*vy) / vv))
    const cx = a.x + t*vx, cy = a.y + t*vy
    return Math.hypot(p.x - cx, p.y - cy)
  }
  const dX = segDist({x:xA.x,y:xA.y}, {x:xB.x,y:xB.y}, P)
  const dY = segDist({x:yA.x,y:yA.y}, {x:yB.x,y:yB.y}, P)
  const dZ = segDist({x:zA.x,y:zA.y}, {x:zB.x,y:zB.y}, P)
  let axis: Axis = 'x'
  if (dY <= dX && dY <= dZ) axis = 'y'
  else if (dZ <= dX && dZ <= dY) axis = 'z'
  return { axis, dX, dY, dZ }
}

// ---- AABB helpers ----
export function bboxCorners(b: Aabb): Position[] {
  const corners: Position[] = []
  ;[b.min.x, b.max.x].forEach(x => {
    ;[b.min.y, b.max.y].forEach(y => {
      ;[b.min.z, b.max.z].forEach(z => corners.push({ x, y, z }))
    })
  })
  return corners
}

export function bboxScreenRect(
  camera: CameraView,
  rect: DOMRect,
  corners: Position[],
): ScreenRect {
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity
  for (const c of corners) {
    const v = new Vector3(c.x + 0.5, c.y + 0.5, c.z + 0.5)
    const p = worldToScreen(camera, v, rect)
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y
    if (p.y > maxY) maxY = p.y
  }
  return { minX, maxX, minY, maxY }
}

export function rightFaceCenter(b: Aabb): Position {
  return {
    x: b.max.x + 0.5,
    y: (b.min.y + b.max.y) / 2 + 0.5,
    z: (b.min.z + b.max.z) / 2 + 0.5,
  }
}

// ---- Screen layout helpers ----
export function clampWidgetCenter(
  x: number,
  y: number,
  rect: DOMRect,
  widgetWidthPx: number,
  widgetHeightPx: number,
  paddingPx: number,
) {
  const halfW = widgetWidthPx / 2
  const halfH = widgetHeightPx / 2
  const minX = paddingPx + halfW
  const maxX = rect.width - paddingPx - halfW
  const minY = paddingPx + halfH
  const maxY = rect.height - paddingPx - halfH
  return {
    x: Math.min(Math.max(x, minX), maxX),
    y: Math.min(Math.max(y, minY), maxY),
  }
}

// Clamp a world-space point so that its projected screen position lies within a padded screen rect.
// Returns a new world-space point at the same camera-relative depth as the input.
export function clampWorldPointToScreenBounds(
  camera: CameraView,
  rect: DOMRect,
  world: Vector3Like,
  paddingPx: number,
): Vector3 {
  // Project to screen to obtain NDC depth and pixel position
  const sp = worldToScreen(camera, world, rect)
  // Clamp pixel coordinates to padded rect
  const minX = paddingPx
  const maxX = rect.width - paddingPx
  const minY = paddingPx
  const maxY = rect.height - paddingPx
  const cx = Math.min(Math.max(sp.x, minX), maxX)
  const cy = Math.min(Math.max(sp.y, minY), maxY)
  // Reconstruct world point at same NDC depth
  return screenToWorldAtDepth(camera, cx, cy, sp.z, rect)
}

// Choose a face-center anchor for a bbox whose projection is closest to the screen center,
// then push outward along the face normal by `outwardWU` so widgets are not intersecting the bbox.
export function bestFaceAnchorNearScreenCenter(
  camera: CameraView,
  rect: DOMRect,
  b: Aabb,
  outwardWU: number,
): Position {
  const cx = (b.min.x + b.max.x) / 2 + 0.5
  const cy = (b.min.y + b.max.y) / 2 + 0.5
  const cz = (b.min.z + b.max.z) / 2 + 0.5
  const faces: Array<{ p: Position; nx: number; ny: number; nz: number }> = [
    { p: { x: b.max.x + 0.5, y: cy, z: cz }, nx: +1, ny: 0, nz: 0 },
    { p: { x: b.min.x + 0.5, y: cy, z: cz }, nx: -1, ny: 0, nz: 0 },
    { p: { x: cx, y: b.max.y + 0.5, z: cz }, nx: 0, ny: +1, nz: 0 },
    { p: { x: cx, y: b.min.y + 0.5, z: cz }, nx: 0, ny: -1, nz: 0 },
    { p: { x: cx, y: cy, z: b.max.z + 0.5 }, nx: 0, ny: 0, nz: +1 },
    { p: { x: cx, y: cy, z: b.min.z + 0.5 }, nx: 0, ny: 0, nz: -1 },
  ]
  let best = faces[0]
  let bestDist = Infinity
  const scx = rect.width / 2
  const scy = rect.height / 2
  for (const f of faces) {
    const v = new Vector3(f.p.x, f.p.y, f.p.z)
    const sp = worldToScreen(camera, v, rect)
    const dx = sp.x - scx
    const dy = sp.y - scy
    const d2 = dx * dx + dy * dy
    if (d2 < bestDist) { bestDist = d2; best = f }
  }
  return {
    x: best.p.x + best.nx * outwardWU,
    y: best.p.y + best.ny * outwardWU,
    z: best.p.z + best.nz * outwardWU,
  }
}

// A stable variant that prefers keeping the previously chosen face unless a new face
// reduces distance-to-screen-center by more than `hysteresisPx`.
export function stableFaceAnchorNearScreenCenter(
  camera: CameraView,
  rect: DOMRect,
  b: Aabb,
  outwardWU: number,
  prevFace: { nx: number; ny: number; nz: number } | null,
  hysteresisPx = 64,
): { anchor: Position; face: { nx: number; ny: number; nz: number } } {
  const cx = (b.min.x + b.max.x) / 2 + 0.5
  const cy = (b.min.y + b.max.y) / 2 + 0.5
  const cz = (b.min.z + b.max.z) / 2 + 0.5
  const faces: Array<{ p: Position; nx: number; ny: number; nz: number; dist2: number }> = [
    { p: { x: b.max.x + 0.5, y: cy, z: cz }, nx: +1, ny: 0, nz: 0, dist2: 0 },
    { p: { x: b.min.x + 0.5, y: cy, z: cz }, nx: -1, ny: 0, nz: 0, dist2: 0 },
    { p: { x: cx, y: b.max.y + 0.5, z: cz }, nx: 0, ny: +1, nz: 0, dist2: 0 },
    { p: { x: cx, y: b.min.y + 0.5, z: cz }, nx: 0, ny: -1, nz: 0, dist2: 0 },
    { p: { x: cx, y: cy, z: b.max.z + 0.5 }, nx: 0, ny: 0, nz: +1, dist2: 0 },
    { p: { x: cx, y: cy, z: b.min.z + 0.5 }, nx: 0, ny: 0, nz: -1, dist2: 0 },
  ]
  const scx = rect.width / 2
  const scy = rect.height / 2
  for (const f of faces) {
    const v = new Vector3(f.p.x, f.p.y, f.p.z)
    const sp = worldToScreen(camera, v, rect)
    const dx = sp.x - scx
    const dy = sp.y - scy
    f.dist2 = dx * dx + dy * dy
  }
  // Best by distance
  faces.sort((a, b2) => a.dist2 - b2.dist2)
  const best = faces[0]
  // Try to keep previous face if not significantly worse
  let chosen = best
  if (prevFace) {
    const prev = faces.find(f => f.nx === prevFace.nx && f.ny === prevFace.ny && f.nz === prevFace.nz)
    if (prev) {
      const dBest = Math.sqrt(best.dist2)
      const dPrev = Math.sqrt(prev.dist2)
      if (dPrev <= dBest + hysteresisPx) chosen = prev
    }
  }
  return {
    anchor: {
      x: chosen.p.x + chosen.nx * outwardWU,
      y: chosen.p.y + chosen.ny * outwardWU,
      z: chosen.p.z + chosen.nz * outwardWU,
    },
    face: { nx: chosen.nx, ny: chosen.ny, nz: chosen.nz }
  }
}
